import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import type { ShippingRouteFilterInput } from '~/graphql/graphql';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';

export const useGetShippingRoutes = (
  page: number,
  size: number,
  filter?: ShippingRouteFilterInput,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const query = graphql(`
    query GetShippingRoutes(
      $pageInput: PageInput!
      $sort: [SortInput!]
      $filter: ShippingRouteFilterInput
    ) {
      shippingRoutes(pageInput: $pageInput, sort: $sort, filter: $filter) {
        content {
          id
          organizationId
          status
          category
          forwarder
          forwarderEmail
          hasForwarderContact
          transitDays
          freeDays
          notes
          expirationDate
          incotermLoading
          incotermDischarge
          portOfLoading {
            id
            code
            name
            city
            country
            type
          }
          originDelivery {
            id
            code
            name
            city
            country
            type
          }
          portOfDischarge {
            id
            code
            name
            city
            country
            type
          }
          destinationDelivery {
            id
            code
            name
            city
            country
            type
          }
          freightCost {
            amount
            currency
          }
          terminalHandlingCharge {
            amount
            currency
          }
          gassingCost {
            amount
            currency
          }
          additionalCostsPerContainer {
            amount
            currency
          }
          additionalCostsPerShipment {
            amount
            currency
          }
        }
        paging {
          totalPages
          totalElements
          number
          size
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    [
      'ShippingRoutes',
      String(page),
      String(size),
      JSON.stringify(filter ?? null),
      JSON.stringify(sort ?? null),
    ],
    {
      pageInput: { page, size },
      sort: sort ?? null,
      filter,
    },
    options
  );
};

export const useGetPorts = () => {
  const query = graphql(`
    query GetPorts {
      ports(active: true) {
        id
        code
        name
        city
        country
        type
        active
      }
    }
  `);

  return useGraphqlQuery(query, ['Ports']);
};

export const useGetAllPorts = () => {
  const query = graphql(`
    query GetAllPorts {
      ports {
        id
        code
        name
        city
        country
        type
        active
      }
    }
  `);

  return useGraphqlQuery(query, ['AllPorts']);
};

export const useGetDutyCosts = () => {
  const query = graphql(`
    query GetDutyCosts {
      dutyCosts {
        id
        htsNumber
        description
        dutyAdValoremRate
        dutySpecificRate
        dutySpecificUnit
        section301Rate
        adCvdRate
        adCvdType
        adCvdDescription
        softwoodLumberFeeMbf
        softwoodLumberFeeCbm
        currencyCode
        originCountryCode
        gspEligible
        conditional
        active
      }
    }
  `);

  return useGraphqlQuery(query, ['DutyCosts']);
};

export const useGetShippingRouteCategories = () => {
  const query = graphql(`
    query GetShippingRouteCategories {
      shippingRouteCategories
    }
  `);

  return useGraphqlQuery(query, ['ShippingRouteCategories'], undefined, {
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetShippingAddOnCategories = () => {
  const query = graphql(`
    query GetShippingAddOnCategories {
      shippingAddOnCategories
    }
  `);

  return useGraphqlQuery(query, ['ShippingAddOnCategories']);
};

export const useGetLogisticsIncoterms = () => {
  const query = graphql(`
    query GetLogisticsIncoterms {
      logisticsIncoterms
    }
  `);

  return useGraphqlQuery(query, ['LogisticsIncoterms'], undefined, {
    staleTime: Infinity,
    placeholderData: {
      logisticsIncoterms: [
        'FCA',
        'FOB',
        'CFR',
        'CIF',
        'DAP',
        'DDP',
        'FCA_COMBO',
        'DDP_COMBO',
        'DAP_COMBO',
      ],
    },
  });
};

export const useCreateShippingRoute = () => {
  const mutation = graphql(`
    mutation CreateShippingRoute($input: CreateShippingRouteInput!) {
      createShippingRoute(input: $input) {
        id
        organizationId
        status
        category
        incotermLoading
        incotermDischarge
        portOfLoading {
          id
          code
          name
        }
        portOfDischarge {
          id
          code
          name
        }
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingRoutes']);
};

export const useUpdateShippingRoute = () => {
  const mutation = graphql(`
    mutation UpdateShippingRoute($id: ID!, $input: UpdateShippingRouteInput!) {
      updateShippingRoute(id: $id, input: $input) {
        id
        organizationId
        status
        category
        incotermLoading
        incotermDischarge
        portOfLoading {
          id
          code
          name
        }
        portOfDischarge {
          id
          code
          name
        }
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingRoutes']);
};

export const useRequestShippingRoute = () => {
  const mutation = graphql(`
    mutation RequestShippingRoute($input: CreateShippingRouteInput!) {
      requestShippingRoute(input: $input) {
        id
        organizationId
        status
        category
        hasForwarderContact
        incotermLoading
        incotermDischarge
        portOfLoading {
          id
          code
          name
        }
        portOfDischarge {
          id
          code
          name
        }
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingRoutes']);
};

export const useGetShippingAddOns = (variables?: {
  hidden?: boolean | null;
}) => {
  const query = graphql(`
    query GetShippingAddOns($hidden: Boolean) {
      shippingAddOns(hidden: $hidden) {
        id
        organizationId
        name
        description
        category
        billingUnit
        cost
        currency
        active
        hidden
        isPlatformCustomization
      }
    }
  `);

  const hidden = variables?.hidden ?? null;
  return useGraphqlQuery(query, ['ShippingAddOns', String(hidden)], { hidden });
};

export const useCreateShippingAddOn = () => {
  const mutation = graphql(`
    mutation CreateShippingAddOn($input: CreateShippingAddOnInput!) {
      createShippingAddOn(input: $input) {
        id
        organizationId
        name
        description
        category
        billingUnit
        cost
        currency
        active
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useUpdateShippingAddOn = () => {
  const mutation = graphql(`
    mutation UpdateShippingAddOn($id: ID!, $input: UpdateShippingAddOnInput!) {
      updateShippingAddOn(id: $id, input: $input) {
        id
        organizationId
        name
        description
        category
        billingUnit
        cost
        currency
        active
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useDeleteShippingAddOn = () => {
  const mutation = graphql(`
    mutation DeleteShippingAddOn($id: ID!) {
      deleteShippingAddOn(id: $id)
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useCustomizePlatformShippingAddOn = () => {
  const mutation = graphql(`
    mutation CustomizePlatformShippingAddOn(
      $platformAddOnId: ID!
      $input: UpdateShippingAddOnInput!
    ) {
      customizePlatformShippingAddOn(
        platformAddOnId: $platformAddOnId
        input: $input
      ) {
        id
        organizationId
        name
        description
        category
        billingUnit
        cost
        currency
        active
        hidden
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useHidePlatformShippingAddOn = () => {
  const mutation = graphql(`
    mutation HidePlatformShippingAddOn($platformAddOnId: ID!) {
      hidePlatformShippingAddOn(platformAddOnId: $platformAddOnId) {
        id
        organizationId
        name
        category
        hidden
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useHideShippingAddOn = () => {
  const mutation = graphql(`
    mutation HideShippingAddOn($id: ID!) {
      hideShippingAddOn(id: $id) {
        id
        organizationId
        name
        category
        hidden
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useHideShippingAddOnCategory = () => {
  const mutation = graphql(`
    mutation HideShippingAddOnCategory($category: String!) {
      hideShippingAddOnCategory(category: $category)
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};

export const useUnhideShippingAddOn = () => {
  const mutation = graphql(`
    mutation UnhideShippingAddOn($id: ID!) {
      unhideShippingAddOn(id: $id) {
        id
        hidden
      }
    }
  `);

  return useGraphqlMutation(mutation, ['ShippingAddOns']);
};
